import type {
  CompareOperator,
  Feature,
  FeatureValue,
  InterpretationRule,
  RuleCondition,
  TriggeredRule,
} from './rules.js';

function includesValue(container: FeatureValue, value: FeatureValue): boolean {
  if (typeof container === 'string' && typeof value === 'string') {
    return container.includes(value);
  }
  if (Array.isArray(container) && typeof value === 'string') {
    return container.includes(value);
  }
  return false;
}

function compareValues(left: FeatureValue, operator: CompareOperator, right: FeatureValue): boolean {
  switch (operator) {
    case 'eq':
      return left === right;
    case 'neq':
      return left !== right;
    case 'gt':
    case 'gte':
    case 'lt':
    case 'lte': {
      if (typeof left !== 'number' || typeof right !== 'number') return false;
      if (operator === 'gt') return left > right;
      if (operator === 'gte') return left >= right;
      if (operator === 'lt') return left < right;
      return left <= right;
    }
    case 'includes':
      return includesValue(left, right);
  }
}

export function evaluateCondition(
  condition: RuleCondition,
  featureMap: ReadonlyMap<string, Feature>,
): boolean {
  if ('all' in condition) {
    return condition.all.every((child) => evaluateCondition(child, featureMap));
  }
  if ('any' in condition) {
    return condition.any.some((child) => evaluateCondition(child, featureMap));
  }
  if ('not' in condition) {
    return !evaluateCondition(condition.not, featureMap);
  }
  if ('featureExists' in condition) {
    return featureMap.has(condition.featureExists);
  }
  if ('compareFeatures' in condition) {
    const left = featureMap.get(condition.compareFeatures.leftFeatureId);
    const right = featureMap.get(condition.compareFeatures.rightFeatureId);
    if (!left || !right) return false;
    return condition.compareFeatures.operator === 'eq' ? left.value === right.value : left.value !== right.value;
  }

  const feature = featureMap.get(condition.compare.featureId);
  if (!feature) return false;
  return compareValues(feature.value, condition.compare.operator, condition.compare.value);
}

function collectConditionFeatureIds(condition: RuleCondition, output = new Set<string>()): Set<string> {
  if ('all' in condition) {
    condition.all.forEach((child) => collectConditionFeatureIds(child, output));
  } else if ('any' in condition) {
    condition.any.forEach((child) => collectConditionFeatureIds(child, output));
  } else if ('not' in condition) {
    collectConditionFeatureIds(condition.not, output);
  } else if ('featureExists' in condition) {
    output.add(condition.featureExists);
  } else if ('compareFeatures' in condition) {
    output.add(condition.compareFeatures.leftFeatureId);
    output.add(condition.compareFeatures.rightFeatureId);
  } else {
    output.add(condition.compare.featureId);
  }
  return output;
}

export function evaluateRules(
  features: readonly Feature[],
  rules: readonly InterpretationRule[],
): TriggeredRule[] {
  const featureMap = new Map(features.map((feature) => [feature.id, feature] as const));
  const scopes = new Set(features.map((feature) => feature.scope));

  return rules
    .filter((rule) => rule.scopes.some((scope) => scopes.has(scope)))
    .filter((rule) => evaluateCondition(rule.conditions, featureMap))
    .map((rule) => {
      const relevant = [...collectConditionFeatureIds(rule.conditions)]
        .map((id) => featureMap.get(id))
        .filter((feature): feature is Feature => Boolean(feature));

      return {
        rule,
        evidenceRefs: [...new Set(relevant.flatMap((feature) => feature.evidenceRefs))].sort(),
        evidenceGroups: [...new Set(relevant.map((feature) => feature.evidenceGroup))].sort(),
      };
    })
    .sort((a, b) => b.rule.priority - a.rule.priority || b.rule.weight - a.rule.weight || a.rule.id.localeCompare(b.rule.id));
}
